/*
 * Talla obligatoria — Mejoras UX Rivera (punto 1, septiembre 2026)
 *
 * El Liquid ya pinta la ficha sin talla marcada: el formulario lleva
 * data-talla-pendiente="true", el campo id va vacío y el botón dice
 * "Selecciona tu talla". Este archivo se encarga de lo demás:
 *
 *  - no deja añadir mientras falte la talla y señala dónde elegirla;
 *  - devuelve el botón a su estado normal en cuanto se elige talla;
 *  - conecta las tallas de la barra fija de móvil con las de la ficha;
 *  - lleva la talla a la ficha de otro color (?talla=38) y la aplica al llegar;
 *  - en pre-venta, cambia el botón que la app oculta en la barra por uno
 *    que lleva al de "Compra anticipada" de la ficha.
 *
 * Todo va delegado en document, así que sirve igual para la vista rápida.
 * Los selectores y atributos que lee son los de Stiletto 4.0.1 (theme.js).
 */
(() => {
  const SEL = {
    contenedor: '[data-section-type]',
    formulario: 'form[data-product-form]',
    selectOpcion: '.product__variants-wrapper select[name^="options"]',
    botonAnadir: '[data-add-to-cart]',
    textoBoton: '[data-add-to-cart-text]',
    selectId: 'select[name="id"]',
    tallasBarra: '[data-sticky-tallas]',
    tallaBarra: '[data-sticky-talla]',
    aviso: '[data-talla-aviso]',
    variantes: '.product__variants-wrapper',
    precio: '[data-price]',
    enlacesColor: '#moddo__colores_modelo a[href], [data-sibling-products] a[href]'
  };

  const contenedorDe = el => el.closest(SEL.contenedor) || document;
  const formularioDe = cont => cont.querySelector(SEL.formulario);

  // Selects de opciones que empezaron sin valor (llevan la opción vacía de "Selecciona tu talla")
  const selectsConTalla = cont =>
    [...cont.querySelectorAll(SEL.selectOpcion)].filter(sel => sel.querySelector('option[value=""]'));

  const chipsDe = (cont, sel) => [
    ...cont.querySelectorAll(`${SEL.variantes} [data-product-option="${sel.id}"] .product__chip`)
  ];

  // Guarda el precio inicial para poder reponerlo si el tema lo cambia a "No disponible"
  const recordarPrecios = cont => {
    cont.querySelectorAll(SEL.precio).forEach(el => {
      if (!('tallaPrecio' in el.dataset)) el.dataset.tallaPrecio = el.innerHTML;
    });
  };

  const destacar = el => {
    el.classList.remove('talla-destacada');
    void el.offsetWidth; // reinicia la animación si se pulsa varias veces
    el.classList.add('talla-destacada');
  };

  // --- Intento de añadir sin talla -----------------------------------------

  function avisar(form, boton) {
    const barra = boton && boton.closest('.sticky-atc-bar');
    const tallasBarra = barra && barra.querySelector(SEL.tallasBarra);
    if (tallasBarra) {
      destacar(tallasBarra);
      return;
    }

    const cont = contenedorDe(form);
    const aviso = cont.querySelector(SEL.aviso);
    const variantes = cont.querySelector(SEL.variantes);
    if (aviso) aviso.hidden = false;
    if (!variantes) return;

    destacar(variantes);
    const { top, bottom } = variantes.getBoundingClientRect();
    if (top < 0 || bottom > window.innerHeight) {
      variantes.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  // Talla elegida, pero el tema aún no la ha recogido (pasa si se pulsa en el
  // primer segundo, antes de que cargue el JSON del producto): se espera a que
  // rellene el campo id y se envía entonces.
  function reintentarEnvio(form, boton, intentos = 0) {
    const selectId = form.querySelector(SEL.selectId);
    if (!selectId || intentos > 25) return;

    if (selectId.value === '') {
      setTimeout(() => reintentarEnvio(form, boton, intentos + 1), 200);
      return;
    }

    const esDelFormulario = boton && boton.form === form && boton.type === 'submit';
    if (typeof form.requestSubmit === 'function') form.requestSubmit(esDelFormulario ? boton : undefined);
    else if (esDelFormulario) boton.click();
  }

  // En captura: se adelanta al envío del tema, que sin variante intentaría
  // añadir una que no existe (o, sin JS, enviaría el formulario vacío).
  document.addEventListener(
    'submit',
    e => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement) || !form.matches(SEL.formulario)) return;

      const pendiente = form.dataset.tallaPendiente === 'true';
      const selectId = form.querySelector(SEL.selectId);
      const sinVariante = !!selectId && selectId.value === '';
      if (!pendiente && !sinVariante) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      const boton = e.submitter || document.activeElement;
      if (pendiente) avisar(form, boton);
      else reintentarEnvio(form, boton);
    },
    true
  );

  // --- Cambio de talla ------------------------------------------------------

  function marcarPendiente(cont, form) {
    // Solo pasa con productos de varias opciones: queda alguna por elegir y el
    // tema ha puesto "No disponible" en el botón y en el precio.
    form.querySelectorAll(`${SEL.botonAnadir}[data-lang-select-size]`).forEach(boton => {
      boton.classList.add('disabled');
      boton.removeAttribute('disabled');
      boton.setAttribute('aria-disabled', 'true');
      const texto = boton.querySelector(SEL.textoBoton);
      if (texto) texto.textContent = boton.dataset.langSelectSize;
    });
    const precio = cont.querySelector('.product__price');
    if (precio) precio.classList.remove('hide');
    cont.querySelectorAll(SEL.precio).forEach(el => {
      if ('tallaPrecio' in el.dataset) el.innerHTML = el.dataset.tallaPrecio;
    });
  }

  // El tema engancha su lógica de variantes cuando termina de cargar el JSON
  // del producto. Si la talla se elige antes, se vuelve a avisar al tema hasta
  // que la recoja (se nota porque rellena el campo id).
  function asegurarTema(cont, form, intentos = 0) {
    const selectId = form.querySelector(SEL.selectId);
    if (!selectId || selectId.value !== '' || intentos > 25) return;

    setTimeout(() => {
      if (selectId.value !== '') return;
      const sel = selectsConTalla(cont).find(s => s.value !== '');
      if (sel) sel.dispatchEvent(new Event('change'));
      asegurarTema(cont, form, intentos + 1);
    }, 200);
  }

  function resolver(cont, form) {
    form.dataset.tallaPendiente = 'false';

    const variantes = cont.querySelector(SEL.variantes);
    if (variantes) variantes.dataset.tallaPendiente = 'false';

    const aviso = cont.querySelector(SEL.aviso);
    if (aviso) aviso.hidden = true;

    cont
      .querySelectorAll('[data-inventory-counter][data-talla-pendiente]')
      .forEach(el => el.removeAttribute('data-talla-pendiente'));

    form
      .querySelectorAll(`${SEL.botonAnadir}[data-lang-select-size]`)
      .forEach(boton => boton.classList.remove('disabled'));

    asegurarTema(cont, form);
  }

  // El tema cambia el texto y el disabled del botón, pero no sus atributos ARIA.
  // Con la talla ya elegida: aria-disabled sigue al disabled real, y sin
  // aria-label el nombre del botón es su texto (también en la copia que hace
  // la app de pre-venta).
  function sincronizarBotones(form) {
    if (form.dataset.tallaPendiente !== 'false') return;
    form.querySelectorAll(`${SEL.botonAnadir}[data-lang-select-size]`).forEach(boton => {
      boton.removeAttribute('aria-label');
      if (boton.disabled) boton.setAttribute('aria-disabled', 'true');
      else boton.removeAttribute('aria-disabled');
    });
  }

  function sincronizarBarra(cont) {
    const tallasBarra = cont.querySelector(SEL.tallasBarra);
    if (!tallasBarra) return;

    tallasBarra.querySelectorAll(SEL.tallaBarra).forEach(boton => {
      const sel = cont.querySelector(`${SEL.variantes} select#${boton.dataset.optionIndex}`);
      if (!sel) return;
      const elegida = sel.value === boton.dataset.optionValue;
      boton.classList.toggle('selected', elegida);
      boton.setAttribute('aria-pressed', elegida ? 'true' : 'false');

      const chip = chipsDe(cont, sel).find(c => c.dataset.optionValue === boton.dataset.optionValue);
      if (chip) boton.classList.toggle('disabled', chip.classList.contains('disabled'));
    });
  }

  // --- Pre-venta (Globo) en la vista rápida --------------------------------
  // La app prepara la vista rápida una sola vez, al abrirla, con la talla que
  // haya marcada. Como ahora no hay ninguna, cada vez que se elige talla se le
  // vuelve a pasar el botón que abrió la vista rápida para que la recalcule.

  let botonVistaRapida = null;

  document.addEventListener(
    'click',
    e => {
      const boton = e.target.closest && e.target.closest('[data-quick-shop-trigger="quick-view"]');
      if (boton) botonVistaRapida = boton;
    },
    true
  );

  function recalcularPreventaVistaRapida(cont, form) {
    const preventa = window.Globo && window.Globo.Preorder;
    if (!botonVistaRapida || !cont.matches('[data-is-quick-view]')) return;
    if (!preventa || typeof preventa.initCollectionQuickview !== 'function') return;

    // Solo si ese botón es del producto que está abierto (la vista rápida
    // también se abre desde otros sitios, como los regalos de la cesta)
    const tarjeta = botonVistaRapida.closest('[preorder-product-element]');
    const idProducto = (form.querySelector('[name="product-id"]') || {}).value;
    const mismoProducto =
      !!tarjeta &&
      ((!!idProducto && tarjeta.getAttribute('d-id') === idProducto) ||
        tarjeta.getAttribute('d-handle') === form.dataset.productHandle);
    if (mismoProducto) preventa.initCollectionQuickview(botonVistaRapida);
  }

  function procesarCambio(sel) {
    const cont = contenedorDe(sel);
    const form = formularioDe(cont);
    if (!form) return;

    // Productos en los que hay que elegir talla; en el resto solo se sincroniza la barra fija
    if (form.dataset.tallaPendiente) {
      if (form.dataset.tallaPendiente === 'true') {
        if (selectsConTalla(cont).some(s => s.value === '')) marcarPendiente(cont, form);
        else resolver(cont, form);
      }

      sincronizarBotones(form);
      if (form.dataset.tallaPendiente === 'false') recalcularPreventaVistaRapida(cont, form);
    }

    sincronizarBarra(cont);
  }

  // El tema lanza el change de las tallas sin burbuja, así que se escucha en
  // captura y se procesa en la siguiente vuelta, cuando el tema ya ha
  // actualizado precio, botón y URL.
  document.addEventListener(
    'change',
    e => {
      const sel = e.target;
      if (!(sel instanceof HTMLSelectElement) || !sel.matches(SEL.selectOpcion)) return;
      recordarPrecios(contenedorDe(sel));
      setTimeout(() => procesarCambio(sel), 0);
    },
    true
  );

  // Elige una talla igual que si la clienta pulsara el chip de la ficha
  function elegir(cont, sel, valor) {
    const chips = chipsDe(cont, sel);
    const chip = chips.find(c => c.dataset.optionValue === valor);
    if (chip) chip.click();

    // Si el tema todavía no escucha los chips, se hace a mano
    if (sel.value !== valor) {
      chips.forEach(c => c.classList.toggle('selected', c === chip));
      sel.value = valor;
      sel.dispatchEvent(new Event('change'));
    }
  }

  // --- Tallas de la barra fija ---------------------------------------------

  document.addEventListener('click', e => {
    const boton = e.target.closest && e.target.closest(SEL.tallaBarra);
    if (!boton) return;
    e.preventDefault();

    const cont = contenedorDe(boton);
    const sel = cont.querySelector(`${SEL.variantes} select#${boton.dataset.optionIndex}`);
    if (sel) elegir(cont, sel, boton.dataset.optionValue);
  });

  // --- Cambio de color: la talla viaja a la otra ficha ---------------------

  document.addEventListener(
    'click',
    e => {
      const enlace = e.target.closest && e.target.closest(SEL.enlacesColor);
      if (!enlace) return;

      const cont = contenedorDe(enlace);
      const form = formularioDe(cont);
      if (!form || form.dataset.tallaPendiente !== 'false') return;

      const sel = selectsConTalla(cont).find(s => s.value !== '');
      if (!sel) return;

      const url = new URL(enlace.href, window.location.href);
      url.searchParams.set('talla', sel.value);
      enlace.href = url.toString();
    },
    true
  );

  function aplicarTallaDeUrl() {
    const url = new URL(window.location.href);
    const talla = url.searchParams.get('talla');
    if (!talla) return;

    url.searchParams.delete('talla');
    window.history.replaceState(window.history.state, '', url.toString());

    const form = document.querySelector(`[data-section-type="product"] ${SEL.formulario}[data-talla-pendiente="true"]`);
    if (!form) return;

    const cont = contenedorDe(form);
    const buscada = talla.trim().toLowerCase();
    for (const sel of selectsConTalla(cont)) {
      const opcion = [...sel.options].find(o => o.value && o.value.trim().toLowerCase() === buscada);
      if (opcion) {
        elegir(cont, sel, opcion.value);
        break;
      }
    }
  }

  // --- Pre-venta en la barra fija ------------------------------------------

  function vigilarPreventa(cont) {
    const barra = cont.querySelector('.sticky-atc-bar');
    const boton = barra && barra.querySelector(SEL.botonAnadir);
    const sustituto = barra && barra.querySelector('[data-sticky-preventa]');
    const zonaCompra = cont.querySelector(`${SEL.formulario} .product-form__item--submit`);
    if (!boton || !sustituto || !zonaCompra) return;

    const actualizar = () => {
      const oculto = boton.classList.contains('gPreorderHidden');
      if (oculto) {
        const botonApp = zonaCompra.querySelector('.gPreorderBtn');
        const texto = botonApp && (botonApp.querySelector(SEL.textoBoton) || botonApp).textContent.trim();
        sustituto.querySelector('[data-sticky-preventa-texto]').textContent = texto || boton.dataset.langAvailable;
      }
      sustituto.hidden = !oculto;
    };

    new MutationObserver(actualizar).observe(boton, { attributes: true, attributeFilter: ['class'] });
    new MutationObserver(actualizar).observe(zonaCompra, { childList: true, subtree: true, characterData: true });
    actualizar();

    sustituto.addEventListener('click', () => {
      zonaCompra.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  }

  // --- Arranque -------------------------------------------------------------

  const iniciar = () => {
    document.querySelectorAll(`${SEL.formulario}[data-talla-pendiente="true"]`).forEach(form => {
      // El Liquid deja el botón desactivado de verdad por si se pulsa antes de
      // que cargue el JS. Ya cargado, se puede pulsar para ver el aviso.
      form
        .querySelectorAll(`${SEL.botonAnadir}[data-lang-select-size]`)
        .forEach(boton => boton.removeAttribute('disabled'));

      recordarPrecios(contenedorDe(form));
    });

    // Barra fija: tallas y botón de pre-venta en todos los productos con tallas,
    // también en talla única
    document.querySelectorAll('.sticky-atc-bar').forEach(barra => {
      const cont = contenedorDe(barra);
      sincronizarBarra(cont);
      vigilarPreventa(cont);
    });

    aplicarTallaDeUrl();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', iniciar);
  } else {
    iniciar();
  }
})();
